import { useEffect, useRef, useState } from 'react';
import {
  Plus, Pencil, Trash2, Eye, BarChart3, Activity, Check, X, Play, AlertTriangle,
  Sparkles, Loader2, RotateCcw,
} from 'lucide-react';
import {
  useCustomMetricsList,
  useCreateCustomMetric,
  useUpdateCustomMetric,
  useDeleteCustomMetric,
  usePreviewMetric,
  useGenerateQuerySpec,
} from '../../hooks/useDashboards';
import { CustomMetricDefinition } from '../../services/dashboards.service';
import { ThemedSelect } from '@components/common/ThemedSelect';

// ==================== Types ====================

interface MetricFormData {
  name: string;
  description: string;
  key: string;
  category: string;
  tabName: string;
  dataSource: string;
  metricType: string;
  querySpec: Record<string, any>;
  chartType: string;
  displayFormat: string;
  thresholds: { good: number; warning: number; critical: number };
  unit: string;
}

const EMPTY_FORM: MetricFormData = {
  name: '',
  description: '',
  key: '',
  category: 'workflow',
  tabName: '',
  dataSource: 'tickets',
  metricType: 'count',
  querySpec: {},
  chartType: 'bar',
  displayFormat: 'number',
  thresholds: { good: 80, warning: 50, critical: 0 },
  unit: 'tickets',
};

const DATA_SOURCES = [
  { value: 'tickets', label: 'Tickets', description: 'Query ticket data' },
  { value: 'automation_executions', label: 'Automations', description: 'Automation execution stats' },
  { value: 'ticket_history', label: 'Ticket History', description: 'Ticket audit trail (coming soon)' },
];

const METRIC_TYPES = [
  { value: 'count', label: 'Count', description: 'Count matching records' },
  { value: 'percentage', label: 'Percentage', description: 'Percentage matching a filter' },
  { value: 'comparison', label: 'Comparison', description: 'Compare multiple filtered counts' },
  { value: 'avg_duration', label: 'Avg Duration', description: 'Average time between two fields' },
  { value: 'sum', label: 'Sum', description: 'Sum of a numeric field' },
];

const CHART_TYPES = [
  { value: 'bar', label: 'Bar Chart' },
  { value: 'line', label: 'Line Chart' },
  { value: 'pie', label: 'Pie Chart' },
  { value: 'gauge', label: 'Gauge' },
  { value: 'number', label: 'Number Card' },
];

const DISPLAY_FORMATS = [
  { value: 'number', label: 'Number' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'duration', label: 'Duration' },
  { value: 'currency', label: 'Currency' },
];

const CATEGORIES = [
  { value: 'workflow', label: 'Workflow' },
  { value: 'custom_field', label: 'Custom Field' },
  { value: 'pipeline', label: 'Pipeline' },
  { value: 'ai', label: 'AI' },
  { value: 'other', label: 'Other' },
];

// Ready-made Query Specification examples, shown as clickable chips only while
// the field is still empty. Clicking one inserts working JSON; the chips hide
// as soon as the spec has content. (The field is JSON — these are valid specs,
// not natural-language questions.)
const QUERY_SPEC_EXAMPLES: { label: string; spec: Record<string, any> }[] = [
  { label: 'Open tickets', spec: { filters: { status: 'open' } } },
  { label: 'Urgent open tickets', spec: { filters: { status: 'open', priority: 'urgent' } } },
  { label: 'By email channel', spec: { filters: { channel: 'email' } } },
  { label: '% resolved', spec: { filterField: 'status', filterValue: 'resolved' } },
  { label: 'Avg resolution time', spec: { startField: 'createdAt', endField: 'resolvedAt' } },
  {
    label: 'Compare urgent vs normal',
    spec: {
      metrics: [
        { label: 'Urgent', filters: { priority: 'urgent' } },
        { label: 'Normal', filters: { priority: 'medium' } },
      ],
    },
  },
];

// ==================== Main Component ====================

/**
 * JSON editor for the query spec. Keeps the raw text the user typed so an
 * invalid draft stays visible and is reported, instead of being dropped while
 * the last valid value (often `{}` = "count everything") is saved in its place.
 */
function QuerySpecEditor({
  value,
  onChange,
  onValidityChange,
  placeholder,
}: {
  value: Record<string, any>;
  onChange: (spec: Record<string, any>) => void;
  onValidityChange: (valid: boolean) => void;
  placeholder: string;
}) {
  const serialized = JSON.stringify(value, null, 2);
  const [text, setText] = useState(serialized);
  const [invalid, setInvalid] = useState(false);
  // The last spec this editor emitted, so a parent update that merely echoes
  // our own change does not reformat the text under the cursor.
  const lastEmitted = useRef(serialized);

  // A fresh editor always starts from a valid spec; clear any invalid flag the
  // parent still holds from a form that was closed mid-edit.
  useEffect(() => {
    onValidityChange(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (serialized === lastEmitted.current) return;
    // Changed from outside (edit, reset, example, AI generate): adopt it.
    lastEmitted.current = serialized;
    setText(serialized);
    setInvalid(false);
    onValidityChange(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized]);

  const handleChange = (next: string) => {
    setText(next);
    try {
      const parsed = next.trim() ? JSON.parse(next) : {};
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('not an object');
      lastEmitted.current = JSON.stringify(parsed, null, 2);
      setInvalid(false);
      onValidityChange(true);
      onChange(parsed);
    } catch {
      setInvalid(true);
      onValidityChange(false);
    }
  };

  return (
    <>
      <textarea
        value={text}
        onChange={(e) => handleChange(e.target.value)}
        rows={6}
        aria-invalid={invalid}
        className={`w-full px-3 py-2 rounded-lg border bg-white dark:bg-gray-900 text-sm font-mono focus:ring-2 focus:ring-primary-500 ${
          invalid ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
        }`}
        placeholder={placeholder}
      />
      {invalid && (
        <p className="text-xs text-red-600 mt-1 dark:text-red-400">
          Invalid JSON. Fix the query specification before saving.
        </p>
      )}
    </>
  );
}

export default function CustomMetricsManager() {
  const { data: metrics, isLoading } = useCustomMetricsList(false);
  const createMetric = useCreateCustomMetric();
  const updateMetric = useUpdateCustomMetric();
  const deleteMetric = useDeleteCustomMetric();
  const previewMetric = usePreviewMetric();
  const generateQuerySpec = useGenerateQuerySpec();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<MetricFormData>(EMPTY_FORM);
  const [previewResult, setPreviewResult] = useState<any>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [querySpecValid, setQuerySpecValid] = useState(true);

  // NLP Query Generation state
  const [nlpInput, setNlpInput] = useState('');
  const [nlpError, setNlpError] = useState<string | null>(null);
  const [nlpSuccess, setNlpSuccess] = useState<string | null>(null);

  // ---- Handlers ----

  const handleCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setPreviewResult(null);
    setPreviewError(null);
    setNlpInput('');
    setNlpError(null);
    setNlpSuccess(null);
    setShowForm(true);
  };

  const handleGenerateFromNL = () => {
    if (!nlpInput.trim()) return;
    setNlpError(null);
    setNlpSuccess(null);

    generateQuerySpec.mutate(
      {
        naturalLanguage: nlpInput.trim(),
        dataSource: form.dataSource,
        metricType: form.metricType,
      },
      {
        onSuccess: (response) => {
          if (response.success && response.querySpec) {
            const confidence = typeof response.confidence === 'number' ? response.confidence : 1;
            const warnings = response.warnings ?? [];

            // Low confidence means the model couldn't map the description to an
            // available field (e.g. a field this tenant doesn't have). Don't
            // overwrite the JSON with a low-confidence guess — surface why and
            // let the user rephrase or edit manually.
            if (confidence < 0.5) {
              setNlpError(
                response.explanation
                  ? `Low confidence — not applied: ${response.explanation}`
                  : "Couldn't confidently match that to your available fields. Try naming a field like status or priority, or edit the JSON below.",
              );
            } else {
              updateField('querySpec', response.querySpec);
              const note = warnings.length ? ` (note: ${warnings.join('; ')})` : '';
              setNlpSuccess(
                (response.explanation || 'Query specification generated successfully') + note,
              );
            }
            // Intentionally keep nlpInput so the user can refine their
            // description instead of retyping it from scratch.
          } else {
            setNlpError(response.error || 'Failed to generate query specification');
          }
        },
        onError: (err: any) => {
          setNlpError(err?.message || 'Failed to generate query specification');
        },
      },
    );
  };

  const handleEdit = (metric: CustomMetricDefinition) => {
    setEditingId(metric.id);
    setForm({
      name: metric.name,
      description: metric.description || '',
      key: metric.key,
      category: metric.category || 'workflow',
      tabName: metric.tabName || '',
      dataSource: metric.dataSource,
      metricType: metric.metricType,
      querySpec: metric.querySpec || {},
      chartType: metric.chartType || 'bar',
      displayFormat: metric.displayFormat || 'number',
      thresholds: (metric.thresholds || { good: 80, warning: 50, critical: 0 }) as any,
      unit: metric.unit || 'tickets',
    });
    setPreviewResult(null);
    setPreviewError(null);
    setShowForm(true);
  };

  const handleDelete = (id: string) => {
    if (!confirm('Delete this custom metric? This cannot be undone.')) return;
    deleteMetric.mutate(id);
  };

  const handleSave = () => {
    // Fields the BFF accepts on BOTH create and update (UpdateCustomMetricDto
    // whitelist). `tabName` is always sent trimmed ("" clears it — BFF nulls it).
    const mutable = {
      name: form.name,
      description: form.description || undefined,
      category: form.category,
      tabName: form.tabName.trim(),
      querySpec: form.querySpec,
      chartType: form.chartType,
      displayFormat: form.displayFormat,
      thresholds: form.thresholds,
      unit: form.unit,
    };

    if (editingId) {
      // key/dataSource/metricType are set at creation and are NOT part of
      // UpdateCustomMetricDto — sending them triggers a 400 "property … should
      // not exist" from the BFF's forbidNonWhitelisted ValidationPipe.
      updateMetric.mutate(
        { id: editingId, ...mutable },
        { onSuccess: () => setShowForm(false) },
      );
    } else {
      // Create additionally sets the immutable identity/shape fields.
      createMetric.mutate(
        { ...mutable, key: form.key, dataSource: form.dataSource, metricType: form.metricType } as any,
        { onSuccess: () => setShowForm(false) },
      );
    }
  };

  // Reset the form: revert to the metric's saved values when editing, or clear
  // to defaults when creating. Only touches this form's local state.
  const handleReset = () => {
    setPreviewResult(null);
    setPreviewError(null);
    const original = editingId ? (metrics || []).find((m) => m.id === editingId) : null;
    if (original) {
      setForm({
        name: original.name,
        description: original.description || '',
        key: original.key,
        category: original.category || 'workflow',
        tabName: original.tabName || '',
        dataSource: original.dataSource,
        metricType: original.metricType,
        querySpec: original.querySpec || {},
        chartType: original.chartType || 'bar',
        displayFormat: original.displayFormat || 'number',
        thresholds: (original.thresholds || { good: 80, warning: 50, critical: 0 }) as any,
        unit: original.unit || 'tickets',
      });
    } else {
      setForm(EMPTY_FORM);
    }
  };

  const handlePreview = () => {
    setPreviewResult(null);
    setPreviewError(null);
    previewMetric.mutate(
      {
        dataSource: form.dataSource,
        metricType: form.metricType,
        querySpec: form.querySpec,
      },
      {
        onSuccess: (data) => setPreviewResult(data),
        onError: (err: any) => setPreviewError(err?.message || 'Preview failed'),
      },
    );
  };

  const updateField = <K extends keyof MetricFormData>(key: K, value: MetricFormData[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const autoKey = (name: string) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  };

  // ---- Render ----

  if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
        <div className="h-48 bg-gray-200 dark:bg-gray-700 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Custom Metrics</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Define organization-specific KPIs tied to tickets, automations, and workflows
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Metric
        </button>
      </div>

      {/* Metric Form */}
      {showForm && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                {editingId ? 'Edit Metric' : 'Create New Metric'}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Fields marked <span className="text-red-500">*</span> are required — they stay highlighted until filled.
              </p>
            </div>
            <button onClick={() => setShowForm(false)} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
              <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Basic info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => {
                  updateField('name', e.target.value);
                  if (!editingId) updateField('key', autoKey(e.target.value));
                }}
                placeholder="e.g., Auto vs Manual Quote Ratio"
                className={`w-full px-3 py-2 rounded-lg border bg-white dark:bg-gray-900 text-sm focus:ring-2 focus:ring-primary-500 ${
                  form.name.trim()
                    ? 'border-gray-300 dark:border-gray-600'
                    : 'border-amber-400 dark:border-amber-500 ring-1 ring-amber-200 dark:ring-amber-500/40'
                }`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Key (unique identifier) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.key}
                onChange={(e) => updateField('key', e.target.value)}
                placeholder="auto_vs_manual_quote"
                className={`w-full px-3 py-2 rounded-lg border bg-white dark:bg-gray-900 text-sm font-mono focus:ring-2 focus:ring-primary-500 ${
                  form.key.trim()
                    ? 'border-gray-300 dark:border-gray-600'
                    : 'border-amber-400 dark:border-amber-500 ring-1 ring-amber-200 dark:ring-amber-500/40'
                }`}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Describe what this metric measures..."
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Data Source & Metric Type */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data Source</label>
              <ThemedSelect
                value={form.dataSource}
                onChange={(e) => updateField('dataSource', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm focus:ring-2 focus:ring-primary-500"
              >
                {DATA_SOURCES.map((ds) => (
                  <option key={ds.value} value={ds.value}>{ds.label}</option>
                ))}
              </ThemedSelect>
              <p className="text-xs text-gray-400 mt-1 dark:text-gray-500">
                {DATA_SOURCES.find((d) => d.value === form.dataSource)?.description}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Metric Type</label>
              <ThemedSelect
                value={form.metricType}
                onChange={(e) => updateField('metricType', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm focus:ring-2 focus:ring-primary-500"
              >
                {METRIC_TYPES.map((mt) => (
                  <option key={mt.value} value={mt.value}>{mt.label}</option>
                ))}
              </ThemedSelect>
              <p className="text-xs text-gray-400 mt-1 dark:text-gray-500">
                {METRIC_TYPES.find((m) => m.value === form.metricType)?.description}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
              <ThemedSelect
                value={form.category}
                onChange={(e) => updateField('category', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm focus:ring-2 focus:ring-primary-500"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </ThemedSelect>
            </div>
          </div>

          {/* Reports tab grouping */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reports Tab Name</label>
            <input
              type="text"
              value={form.tabName}
              onChange={(e) => updateField('tabName', e.target.value)}
              placeholder="e.g., Sales Won (defaults to the metric name)"
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm focus:ring-2 focus:ring-primary-500"
            />
            <p className="text-xs text-gray-400 mt-1 dark:text-gray-500">
              Each custom metric gets its <span className="font-medium">own tab</span> on the Reports page. This sets that tab's label — leave blank to use the metric name.
            </p>
          </div>

          {/* NLP Query Generation */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Describe your filter in plain English
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={nlpInput}
                  onChange={(e) => setNlpInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleGenerateFromNL()}
                  placeholder='e.g., "tickets where status is open and priority is urgent"'
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm focus:ring-2 focus:ring-primary-500"
                />
                <button
                  onClick={handleGenerateFromNL}
                  disabled={generateQuerySpec.isPending || !nlpInput.trim()}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {generateQuerySpec.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  {generateQuerySpec.isPending ? 'Generating...' : 'Generate'}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1 dark:text-gray-500">
                AI-powered: describe what you want to filter and we'll generate the JSON for you
              </p>
            </div>

            {nlpSuccess && (
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <p className="text-sm text-green-700 dark:text-green-400">{nlpSuccess}</p>
              </div>
            )}

            {nlpError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                <p className="text-sm text-red-700 dark:text-red-400">{nlpError}</p>
              </div>
            )}
          </div>

          {/* Query Spec Editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Query Specification (JSON)
            </label>
            <QuerySpecEditor
              // Remount per form session so a stale invalid draft never
              // carries over from one metric to the next.
              key={`${editingId ?? 'new'}:${showForm}`}
              value={form.querySpec}
              onChange={(spec) => updateField('querySpec', spec)}
              onValidityChange={setQuerySpecValid}
              placeholder={`{
  "filters": { "status": "in_progress" },
  "filterField": "priority",
  "filterValue": "urgent"
}`}
            />
            <p className="text-xs text-gray-400 mt-1 dark:text-gray-500">
              Define filters and aggregation. For comparisons, use <code className="text-xs">metrics</code> array. Leave empty <code className="text-xs">{'{}'}</code> to count everything.
            </p>

            {/* Example specs — shown only while the field is empty; they vanish
                once the user writes their own query. */}
            {Object.keys(form.querySpec || {}).length === 0 && (
              <div className="mt-2">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                  Examples — click to insert (hidden once you start writing):
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {QUERY_SPEC_EXAMPLES.map((ex) => (
                    <button
                      key={ex.label}
                      type="button"
                      onClick={() => updateField('querySpec', ex.spec)}
                      title={JSON.stringify(ex.spec)}
                      className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-primary-50 text-primary-700 hover:bg-primary-100 dark:bg-primary-900/30 dark:text-primary-300 dark:hover:bg-primary-900/50 transition-colors"
                    >
                      {ex.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Display options */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Chart Type</label>
              <ThemedSelect
                value={form.chartType}
                onChange={(e) => updateField('chartType', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm focus:ring-2 focus:ring-primary-500"
              >
                {CHART_TYPES.map((ct) => (
                  <option key={ct.value} value={ct.value}>{ct.label}</option>
                ))}
              </ThemedSelect>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Display Format</label>
              <ThemedSelect
                value={form.displayFormat}
                onChange={(e) => updateField('displayFormat', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm focus:ring-2 focus:ring-primary-500"
              >
                {DISPLAY_FORMATS.map((df) => (
                  <option key={df.value} value={df.value}>{df.label}</option>
                ))}
              </ThemedSelect>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Unit</label>
              <input
                type="text"
                value={form.unit}
                onChange={(e) => updateField('unit', e.target.value)}
                placeholder="tickets"
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Good Threshold</label>
              <input
                type="number"
                value={form.thresholds.good}
                onChange={(e) => updateField('thresholds', { ...form.thresholds, good: Number(e.target.value) })}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Preview & Save */}
          <div className="flex items-center gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handlePreview}
              disabled={previewMetric.isPending || !querySpecValid}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
            >
              <Play className="w-4 h-4" />
              {previewMetric.isPending ? 'Running...' : 'Preview'}
            </button>
            <button
              onClick={handleSave}
              disabled={!form.name || !form.key || !querySpecValid || createMetric.isPending || updateMetric.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              <Check className="w-4 h-4" />
              {editingId ? 'Update' : 'Create'}
            </button>
            <button
              type="button"
              onClick={handleReset}
              title={editingId ? 'Revert to the saved values' : 'Clear the form'}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 text-sm rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 text-sm hover:text-gray-800 dark:hover:text-gray-200"
            >
              Cancel
            </button>
          </div>

          {/* Preview Result */}
          {previewResult && (
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <p className="text-sm font-medium text-green-800 dark:text-green-300 mb-2">Preview Result</p>
              <pre className="text-xs text-green-700 dark:text-green-400 overflow-x-auto">
                {JSON.stringify(previewResult, null, 2)}
              </pre>
            </div>
          )}
          {previewError && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
              <p className="text-sm text-red-700 dark:text-red-400">{previewError}</p>
            </div>
          )}
        </div>
      )}

      {/* Metrics List */}
      {(!metrics || metrics.length === 0) && !showForm ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-12 text-center">
          <BarChart3 className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-base font-medium text-gray-900 dark:text-white mb-1">No custom metrics yet</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Create metrics to track organization-specific KPIs like auto vs manual quotes,
            tickets by custom category, or automation success rates.
          </p>
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Your First Metric
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {(metrics || []).map((metric) => (
            <div
              key={metric.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4 flex items-center gap-4"
            >
              <div className={`p-2.5 rounded-lg shrink-0 ${
                metric.isActive
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
              }`}>
                <Activity className="w-5 h-5" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">{metric.name}</h4>
                  <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 font-mono">
                    {metric.key}
                  </span>
                  {!metric.isActive && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400">
                      Inactive
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                  {metric.description || `${metric.dataSource} / ${metric.metricType}`}
                </p>
              </div>

              <div className="flex items-center gap-1 shrink-0">
                <span className="text-xs text-gray-400 dark:text-gray-500 mr-2 hidden sm:inline">
                  {metric.category || 'uncategorized'}
                </span>
                <button
                  onClick={() => handleEdit(metric)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Edit"
                >
                  <Pencil className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 dark:text-gray-500" />
                </button>
                <button
                  onClick={() => handleDelete(metric.id)}
                  className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-500 dark:text-gray-500" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
